import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  sendOtp, verifyOtp, createOrUpdateUser, ensureUserDefaults, getAuthErrorKey, auth, warmRecaptcha,
  setNativeOtpCallbacks,
} from '../services/firebase';
import { useNativePhoneAuth } from '../utils/platform';
import { useApp, markAwaitingName, clearAwaitingName } from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import LangToggle from '../components/LangToggle';
import OtpInput from '../components/OtpInput';
import { LegalInline } from '../components/LegalLinks';
import { extractOtpCode, listenForWebOtpWithRetry } from '../utils/otpAutofill';
import { markPendingLocationPrompt } from '../utils/deliveryTarget';

const STEPS = ['phone', 'otp', 'name'];

export default function Login({ startAt }) {
  const [step, setStep] = useState(startAt || 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const verifyingRef = useRef(false);
  const attemptedOtpRef = useRef('');
  const navigate = useNavigate();
  const { refreshUserProfile, needsName, profileReady, user } = useApp();
  const { t } = useLanguage();

  useEffect(() => {
    if (startAt) setStep(startAt);
  }, [startAt]);

  useEffect(() => {
    if (!useNativePhoneAuth() && (step === 'phone' || step === 'otp')) warmRecaptcha();
  }, [step]);

  useEffect(() => {
    if (user && profileReady && needsName) {
      setStep('name');
    }
  }, [user, profileReady, needsName]);

  useEffect(() => {
    if (user && profileReady && !needsName && step === 'otp') {
      navigate('/', { replace: true });
    }
  }, [user, profileReady, needsName, step, navigate]);

  const visibleSteps = step === 'name' ? STEPS : STEPS.slice(0, 2);
  const stepIndex = visibleSteps.indexOf(step);

  const resetOtpSession = useCallback(() => {
    window.confirmationResult = null;
    window.recaptchaWarmPromise = null;
    window.recaptchaWidgetId = null;
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (err) { /* */ }
      window.recaptchaVerifier = null;
    }
    document.querySelectorAll('[id^="recaptcha-container"]').forEach((el) => el.remove());
    attemptedOtpRef.current = '';
    verifyingRef.current = false;
  }, []);

  const handleSendOtp = async () => {
    if (phone.length < 10) { toast.error(t('login.invalidPhone')); return; }
    setLoading(true);
    try {
      resetOtpSession();
      const sent = await sendOtp(`+91${phone}`);
      if (sent?.autoVerified && auth.currentUser) {
        markAwaitingName(auth.currentUser.uid);
        markPendingLocationPrompt();
        return;
      }
      setOtp('');
      attemptedOtpRef.current = '';
      toast.success(t('login.otpSent'));
      setStep('otp');
    } catch (e) {
      console.error(e);
      resetOtpSession();
      toast.error(t(getAuthErrorKey(e)), { id: 'otp-send-error' });
    } finally { setLoading(false); }
  };

  const completeOtpLogin = useCallback(async (code) => {
    const normalized = extractOtpCode(code);
    if (normalized.length < 6) {
      toast.error(t('login.invalidOtp'), { id: 'otp-verify-error' });
      return;
    }
    if (verifyingRef.current) return;
    if (attemptedOtpRef.current === normalized) return;
    if (!useNativePhoneAuth() && !window.confirmationResult) {
      toast.error(t('authErrors.noOtpSession'), { id: 'otp-verify-error' });
      setStep('phone');
      return;
    }
    attemptedOtpRef.current = normalized;
    verifyingRef.current = true;
    setLoading(true);
    setOtp(normalized);
    try {
      await verifyOtp(normalized);
      markAwaitingName(auth.currentUser?.uid);
      markPendingLocationPrompt();
      // Profile load + name check handled by AppContext (no duplicate Firestore round-trips here).
    } catch (e) {
      console.error(e);
      const key = getAuthErrorKey(e);
      toast.error(t(key), { id: 'otp-verify-error' });
      if (key === 'authErrors.noOtpSession' || key === 'authErrors.codeExpired') {
        resetOtpSession();
        setOtp('');
        attemptedOtpRef.current = '';
        if (key === 'authErrors.noOtpSession') setStep('phone');
      } else if (key === 'authErrors.invalidOtp') {
        attemptedOtpRef.current = '';
      }
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  }, [phone, t, resetOtpSession]);

  const handleOtpChange = (code) => {
    setOtp(code);
    if (code.length < 6) attemptedOtpRef.current = '';
  };

  useEffect(() => {
    if (!useNativePhoneAuth()) return undefined;
    setNativeOtpCallbacks({
      onAutoVerified: async () => {
        const user = auth.currentUser;
        if (!user) {
          toast.error(t('authErrors.default'), { id: 'otp-auto-error' });
          return;
        }
        markAwaitingName(user.uid);
        markPendingLocationPrompt();
      },
      onCodeReceived: (code) => {
        const digits = extractOtpCode(code);
        attemptedOtpRef.current = '';
        setOtp(digits);
        completeOtpLogin(digits);
      },
    });
    return () => setNativeOtpCallbacks({ onAutoVerified: null, onCodeReceived: null });
  }, [completeOtpLogin]);

  useEffect(() => {
    if (step !== 'otp' || useNativePhoneAuth()) return undefined;
    const ac = new AbortController();
    return listenForWebOtpWithRetry((code) => {
      const digits = extractOtpCode(code);
      if (digits.length < 6) return;
      attemptedOtpRef.current = '';
      setOtp(digits);
      completeOtpLogin(digits);
    }, ac.signal);
  }, [step, completeOtpLogin]);

  useEffect(() => {
    if (step === 'name') {
      const timer = setTimeout(() => document.getElementById('login-name-input')?.focus(), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step]);

  const handleSaveName = async () => {
    if (!name.trim()) { toast.error(t('login.emptyName')); return; }
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      const phoneStr = currentUser.phoneNumber || `+91${phone}`;
      await ensureUserDefaults(currentUser.uid, phoneStr);
      await createOrUpdateUser(currentUser.uid, {
        name: name.trim(),
        phone: phoneStr,
        profileComplete: true,
      });
      clearAwaitingName();
      markPendingLocationPrompt();
      await refreshUserProfile(currentUser.uid);
      toast.success(t('login.welcome', { name: name.trim() }));
      navigate('/');
    } catch (e) {
      console.error(e);
      toast.error(t(getAuthErrorKey(e)));
    } finally { setLoading(false); }
  };

  return (
    <div className="app-shell app-shell--white">
      <div className="login-header">
        <LangToggle />
        <div style={{ fontSize: 48, lineHeight: 1 }} aria-hidden>🏘️</div>
        <div className="login-brand">{t('appName')}</div>
        <div className="login-tagline">{t('tagline')}</div>
        <span className="location-badge">📍 {t('town')}</span>
      </div>

      <main style={{ padding: '28px 24px', flex: 1 }} id="main-content">
        <div className="step-dots">
          {visibleSteps.map((s, i) => (
            <div
              key={s}
              className={`step-dot ${i === stepIndex ? 'step-dot--active' : i < stepIndex ? 'step-dot--done' : 'step-dot--pending'}`}
            />
          ))}
        </div>

        {step === 'phone' && (
          <div className="page-enter">
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.3px' }}>{t('login.enterPhone')}</div>
            <p className="text-muted" style={{ marginBottom: 24, lineHeight: 1.5 }}>{t('login.phoneSub')}</p>
            <div className="gap-row" style={{ marginBottom: 20 }}>
              <div className="phone-prefix">🇮🇳 +91</div>
              <input
                type="tel"
                className="phone-input"
                placeholder={t('login.phonePlaceholder')}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                style={{ paddingLeft: 14 }}
                autoComplete="tel-national"
                inputMode="numeric"
              />
            </div>
            <button type="button" className="btn btn-green" onClick={handleSendOtp} disabled={loading || phone.length < 10}>
              {loading ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : t('login.sendOtp')}
            </button>
          </div>
        )}

        {step === 'otp' && (
          <div className="page-enter">
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.3px' }}>{t('login.enterOtp')}</div>
            <p className="text-muted" style={{ marginBottom: 16 }}>
              {t('login.sentTo', { phone })}{' '}
              <button type="button" className="text-link" style={{ background: 'none', border: 'none', font: 'inherit', padding: 0 }} onClick={() => { resetOtpSession(); setOtp(''); setStep('phone'); }}>
                {t('login.change')}
              </button>
            </p>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              onComplete={completeOtpLogin}
              disabled={loading}
              autoFocus
            />
            <button type="button" className="btn btn-green" onClick={() => completeOtpLogin(otp)} disabled={loading || otp.length < 6}>
              {loading ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : t('login.verifyOtp')}
            </button>
            <p className="text-center text-muted mt-16">
              {t('login.noOtp')}{' '}
              <button type="button" className="text-link" style={{ background: 'none', border: 'none', font: 'inherit', padding: 0 }} onClick={handleSendOtp}>
                {t('login.resend')}
              </button>
            </p>
          </div>
        )}

        {step === 'name' && (
          <div className="page-enter">
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.3px' }}>{t('login.yourName')}</div>
            <p className="text-muted" style={{ marginBottom: 24 }}>{t('login.nameSub')}</p>
            <div className="input-wrap">
              <span className="input-icon" aria-hidden>👤</span>
              <input
                id="login-name-input"
                type="text"
                placeholder={t('login.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                autoComplete="name"
              />
            </div>
            <button type="button" className="btn btn-green mt-8" onClick={handleSaveName} disabled={loading || !name.trim()}>
              {loading ? <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : t('login.letsGo')}
            </button>
          </div>
        )}
      </main>

      <LegalInline className="login-legal" />
    </div>
  );
}
